import {Template, Component, Attribute} from '@scoutgg/ftw/source/lib'
import rules from '@scoutgg/scoring-rules'
import template from './stats.jade'
import '@scoutgg/ftw/source/atoms/toggle/toggle'
import '@scoutgg/ftw/source/atoms/icons/icons'
import {Match, Team} from '../../models'
import {sortPlayers} from '../../services/positions'
import oldState from '../../services/create-event'
import getState from '../../services/event-state'
import camelcase from 'camelcase'

function displayTime(seconds) {
  const minutes = Math.floor(seconds / 60)
  seconds = seconds % 60
  if(seconds < 10) {
    seconds = `0${seconds}`
  }
  return `${minutes}:${seconds}`
}

function byRank(a, b, match, eventType) {
  if(!a.stats) a.stats = {}
  if(!b.stats) b.stats = {}
  if(a.stats.pair || b.stats.pair) {
    return (a.stats.pair || Infinity) - (b.stats.pair || Infinity)
  }

  if(match.isRacing) {
    if(!a.stats[eventType] && !b.stats[eventType]) return a.lane - b.lane
    if(a.stats[eventType] === b.stats[eventType]) return 0
    return (a.stats[eventType] || Infinity) - (b.stats[eventType] || Infinity)
  }
  if(!a.stats.rank && !b.stats.rank) return a.lane - b.lane
  if(a.stats.rank === b.stats.rank) return 0
  return (a.stats.rank || Infinity) - (b.stats.rank || Infinity)
}

const hockeyMap = {
  center: 'forward',
  winger: 'forward',
}
const hockeyOrder = [
  'goalkeeper',
  'forward',
  'defender',
]
const sortMethods = {
  hockey({ lineup: { player: a } }, { lineup: { player: b } }) {
    const order = hockeyOrder
    const aPos = hockeyMap[a.position] || a.position
    const bPos = hockeyMap[b.position] || b.position
    var ret = (aPos ? order.indexOf(aPos) : Infinity) -
      (bPos ? order.indexOf(bPos) : Infinity)
    if(ret) return ret
    ret = (a.shirt || Infinity) - (b.shirt || Infinity)
    if(ret) return ret
    ret = a.formalName.localeCompare(b.formalName)
    return ret
  },
  shirt({ lineup: a }, { lineup: b }) {
    return (
      ((a.player.shirt || Infinity) - (b.player.shirt || Infinity)) ||
      sortPlayers(a.player, b.player)
    )
  },
  duel({ lineup: a }, { lineup: b }) {
    return sortPlayers(a.player, b.player, null, a.season.sport)
  },
  rank({ lineup: a }, { lineup: b }) {
    if(!this) return
    return (
      byRank(a, b, this.match, camelcase(eventType)) ||
      sortPlayers(a.player, b.player)
    )
  },
  custom({ lineup: a }, { lineup: b }) {
    return (a.lane > b.lane ? 1 : -1)
  },
}

const doubleStats = [
  'point',
  'rebound',
  'assist',
  'block',
  'steal',
]

const badStats = [
  'forfeit',
  'disqualified',
  'turnover',
  'ownGoal',
  'shootoutCaused',
  'concededGoal',
  'penaltyMinutes',
  'bogey',
  'doubleBogey',
  'water',
  'outOfBounds',
  'threePut',
  'misseedGir',
  'missedPut',
  'bunker',
  'gallop',
  'miss',
  'duckDismissal',
  'noFinish',
  'noFinishRegular',
  'noFinishDownhill',
  'noFinishCombined',
  'juryPoints',
]

const goodValues = [
  '✓',
  '✓✓',
  '+',
  '2x',
  '3x',
]

const badValues = [
  '✗',
  '-',
]

const cautionValues = [
  '=',
]

const editable = {
  basket: [
    'threepointGoal',
    'fieldGoal',
    'freethrowGoal',
    'rebound',
    'assist',
    'steal',
    'block',
    'turnover',
    'point',
  ],
  hockey: [
    'plusMinus',
    'shot',
    'keeperSave',
    'concededGoal',
    'hit',
    'shirt',
    'gameWinningGoal',
    'blockedShot',
    'shootoutGoal',
    'cleanSheet',
  ],
  golf: [
    'holeInOne',
    'betterThanEagle',
    'eagle',
    'birdie',
    'par',
    'bogey',
    'doubleBogey',
    'tripleBogeyOrWorse',
    'noBogeys',
    'threeConsecutiveBirdies',
    'ro64',
    'allFourSub70',
    'bounceBack',
    'rank',
    'remainingHoles',
    'madeCut',
  ],
  rocket_runout: [
    'ballPocketed',
    'scratch',
    'breakAndRun',
    'dryBreakAndRun',
    'ballInHandAndRun',
    'salvageRun',
    'combination',
    'special',
    'perfectGame',
    'foul',
    'remainingFrames',
    'gameScore',
    'partialRun1',
    'partialRun2',
    'partialRun3',
    'partialRun4',
    'partialRun5',
  ],
  cycling: [
    'shirtEarned',
    'aggressive',
    'auxiliary1',
    'auxiliary2',
    'auxiliary3',
  ],
  cycling_vm: [
    'rank',
  ],
  dota2: [
    'firstBlood',
    'kill',
    'assist',
    'deathCore',
    'deathSupport',
    'death',
    'creepKill',
    'creepDeny',
    'towerDamage',
    'roshanKill',
    'roshanKillTeamBonus',
    'aegisSnatched',
    'wardsPlaced',
    'wardsDestroyed',
    'campStack',
    'coreCampStack',
    'runePickup',
    'heroHealing',
    'courierKill',
    'stun',
    'underTwentySeven',
    'underThirtyThree',
    'overFortyFive',
    'overFiftyFive',
    'overSeventy',
  ],
  horses: [
    'batch',
    'lane',
    'rank',
    'gallop',
    'forfeit',
    'prizeMoney',
    'disqualified',
  ],
  cc_ski: [
    'batch',
    'forfeit',
  ],
  curling: [
    'hitPercentage',
  ],
  ski_jump: [
    'distance',
  ],
  biathlon: [
    'forfeit',
  ],
  cricket: [
    'run',
    'ball',
    'concededRun',
    'over',
    'boundary',
    'six',
    'maidenOver',
  ],
  rugby_union: [
    'tackle',
    'meterGained',
    'turnover',
    'turnoverConceded',
  ],
  football: [
    'projectedPoints',
    'shotOnTarget',
    'penaltySave',
    'keeperSave',
    'penaltyMiss',
    'assist',
    'penaltyCaused',
    'foulsDrawn',
    'foulCommited',
    'shootoutCaused',
  ],
  tennis: [
    'doubleFault',
    'ace',
    'breakWon',
    'breakLost',
  ],
  kabaddi: [
    'substitution',
    'touch',
    'raid',
    'raidOut',
    'raidEmpty',
    'raidBonus',
    'raidSuper',
    'tackle',
    'tackleSuper',
    'greenCard',
    'yellowCard',
    'redCard',
  ],
  american_football: [
    'intercepted',
    'reception',
    'fumbleLost',
    'sack',
    'safety',
    'blockedKick',
    'fumbleRecovery',
    'fumbleRecoveryTd',
    'passingYards',
    'passingTd',
    'rushingYards',
    'rushingTd',
    'receivingYards',
    'receivingTd',
    'conversion',
    'interception',
    'returnTd',
    'pointsAllowed',
    'pointsAllowedOverride',
    'defensiveTd',
  ],
  futsal: [
  ],
  alpine: [
    'forfeit',
    'pair',
    'splitRank',
  ],
  baseball: [
    'earnedRun',
    'strikeout',
    'inningPitched',
    'inningThirdsPitched',
    'hitAgainst',
    'lostBase',
    'batterHit',
    'noHitter',
    'completeGame',
    'completeGameShutOut',
    'single',
    'double',
    'triple',
    'homeRun',
    'rbi',
    'scoredRun',
    'baseOnBall',
    'stolenBase',
    'hitByPitch',
    'stealCaught',
    'strikeout',
    'teamWin',
  ],
  mma: [
    'strike',
    'significantStrike',
    'controlTime',
    'takedown',
    'reversal',
    'knockdown',
  ],
  csgo: [
    'kill',
    'killHeadshot',
    'killAssist',
    'killKnife',
    'killGrenade',
    'flashAssist',
    'teamkill',
    'death',
    'entryKill',
    'quad',
    'ace',
    'bombPlanted',
    'bombDefused',
    'targetBombed',
    'roundWin',
    'mapWin',
    'clutch1v1',
    'clutch1v2',
    'clutch1v3',
    'clutch1v4',
    'clutch1v5',
    'roundDifference',
    'compensationPoints',
    'compensationPointsV3',
  ],
  lol: [
    'kill',
    'assist',
    'death',
    'turretTakedownTeam',
    'firstBlood',
    'baronTeamKill',
    'dragonTeamKill',
    'riftHeraldTeamKill',
    'creepKill',
    'win',
    'winInUnderThirtyMinutes',
    'winInUnderThirtyfiveMinutes',
    'dragonSteal',
    'baronSteal',
  ],
  handball: [
    'goal',
    'miss',
    'penaltyMiss',
    'penaltySave',
    'assist',
    'steal',
    'save',
    'block',
    'concededGoal',
    'yellowCard',
    'redCard',
    'twoMinute',
    'teamLoss',
    'teamWin',
    'penaltyInduce',
  ],
  eurovision: [
    'tierLoserPoints',
    'tierWinnerPoints',
    'tripleTwelve',
    'televiewerWinnerPoints',
    'juryWinnerPoints',
    'juryPoints',
    'televiewerPoints',
    'overrideTierWinner',
    'qualified',
  ],
  formula_one: [
  ],
}

const pointGoals = [
  'threepointGoal',
  'fieldGoal',
  'freethrowGoal',
  'point',
  'earnedRun',
]

@Component('sporty')
@Template(template)
@Attribute('match', Match)
@Attribute('team', Team)
@Attribute('bench', Boolean)
@Attribute('edit-mode', Boolean)
@Attribute('context', String)
@Attribute('position', String)
@Attribute('exclude-position', String)
@Attribute('exclude-positions', Array)
@Attribute('batch', Number)
@Attribute('stat', String)
@Attribute('stats', Array)
@Attribute('event-type', String)
export default class Stats extends HTMLElement {
  attached() {
    this.playersPerPosition = {}
    this.update(this.match.load())
    this.oldState.listen(this, true)
    this.state.listen(this, true)
    this.customSort = false
  }
  matchChange() {
    this.playersPerPosition = {}
    this.update(this.match.load())
    this.oldState.listen(this, true)
    this.state.listen(this, true)
  }
  filterLineups(lineups) {
    return lineups
      .filter(lineup => lineup.participating)
      .filter(lineup => !this.team || lineup.team === this.team)
      .filter(lineup => !this.batch || lineup.batch === this.batch)
      .filter(lineup => !this.stat || lineup.stats[this.stat])
      .filter(lineup => !this.stats || this.stats.find(s => lineup.stats[s]))
      .filter(lineup => {
        switch(false) {
          case !this.position:
            return lineup.player.position === this.position
          case !this.excludePosition:
            return lineup.player.position !== this.excludePosition
          case !this.excludePositions:
            return !~this.excludePositions.indexOf(lineup.player.position)
          default:
            return !this.bench === !lineup.isBench
        }
      })
  }

  toggleCustomSort() {
    this.customSort = !this.customSort
    this.render()
  }
  toggleConfirmLineup(lineup) {
    if(lineup.stats) {
      if(!lineup.stats.confirmed) {
        lineup.stats.confirmed = true
      } else {
        lineup.stats.confirmed = false
      }
    }
    this.render()
  }
  teamLineup() {
    const state = (this.match.lockedEvents || []).length
      ? 'removed'
      : 'unchanged'
    const lineups = this.filterLineups(this.match.lineups || []).map(l => {
      return {
        lineup: l,
        state: state,
        original: null,
      }
    })
    const locked = this.filterLineups(this.match.lockedLineups || [])
    const existing = lineups.reduce((obj, item) => {
      obj[item.lineup.player] = item
      return obj
    }, {})
    if(this.match.stopFeeds) {
      for(const lineup of locked) {
        const original = existing[lineup.player]
        var changed = !original
        if(original) {
          const a = original.lineup.stats || {}
          const b = lineup.stats || {}
          if(original.lineup.duration !== lineup.duration) changed = true
          for(const {normal: col} of this.columns) {
            if((a[col] || 0) !== (b[col] || 0)) changed = true
          }
        }
        if(changed) {
          lineups.push({
            lineup,
            state: 'added',
            original,
          })
        } else {
          original.state = 'unchanged'
        }
      }
    }
    const sortFunc
      = lineups[0]?.lineup.lane ? sortMethods.custom
      : this.match.sport === 'basket' ? sortMethods.shirt
      : this.match.sport === 'hockey' ? sortMethods.hockey
      : this.match.isDuel ? sortMethods.duel
      : sortMethods.rank

    lineups.sort(sortFunc)
    return lineups
  }
  lineupsFor(position) {
    if(this.playersPerPosition[position]) return this.playersPerPosition[position]
    if(!(this.lineups && this.lineups.length)) return []
    this.playersPerPosition[position] = this.lineups
      .filter(lineup => lineup.status === 'confirmed')
      .filter(lineup => lineup.player.position === position)
    return this.playersPerPosition[position]
  }
  playtime(lineup) {
    return Math.ceil((+lineup.duration || 0) / 60)
  }
  isGood(lineup, column, value) {
    if(goodValues.includes(value)) return true
    switch(column.normal) {
      case 'stroke':
        return lineup.stats && lineup.stats.rank && value < 70
      case 'hitPercentage':
        return value >= 60
      case 'raid':
        return value >= 10
      case 'tackle':
        return lineup.match.sport === 'kabaddi' && value >= 6
      case 'ace':
        return value >= 15
      case 'televiewerPoints':
        return true
    }
  }
  isBad(lineup, column, value) {
    return (badStats.includes(column.normal) && +value) ||
      value < 0 ||
      badValues.includes(value)
  }
  isCaution(lineup, column, value) {
    return cautionValues.includes(value)
  }
  columnClass(lineup, column) {
    var val = this.calcStat(lineup, column)
    return {
      [column.normal]: true,
      zero: !val,
      good: this.isGood(lineup, column, val),
      bad: this.isBad(lineup, column, val),
      caution: this.isCaution(lineup, column, val),
      double: ~doubleStats.indexOf(column.normal) && val > 9,
    }
  }
  doubleClass(stats) {
    return {
      double: stats.doubleDouble,
      triple: stats.tripleDouble,
      zero: !stats.doubleDouble && !stats.tripleDouble,
    }
  }
  setDuration(lineup, e) {
    if(!e.target.value) return
    lineup.duration = e.target.value * 60
  }
  cleanStats(lineup) {
    if(!lineup.duration) lineup.duration = 0
    if(!lineup.stats) lineup.stats = {}
    const columns = {}
    for(const col of this.columns) columns[col.normal] = col
    ;(editable[this.match.sport] || [])
      .filter(stat => !lineup.stats[stat])
      .filter(stat => columns[stat])
      .filter(stat => columns[stat].type !== 'enum')
      .forEach(stat => (lineup.stats[stat] = 0))
  }
  canEditLineup(lineup) {
    return true
  }
  canEditStats(column) {
    if(!this.editMode) return false
    if(this.match.season.skipCompute) return true
    return (editable[this.match.sport] || []).includes(column.normal)
  }
  setOrder(lineup, value) {
    lineup.lane = value
  }
  setStat(lineup, column, value) {
    if(column.enum) {
      lineup.stats[column.normal] = value
    } else {
      lineup.stats[column.normal] = +value
    }
    if(column.normal === 'rank' && lineup.match.sport === 'horses') {
      delete lineup.stats.bouts
    }
    this.calcStat(lineup, column)
  }
  calcStat(lineup, column) {
    if(pointGoals.includes(column.normal)) this.calcPoints(lineup)
    if(doubleStats.includes(column.normal)) this.calcDoubles(lineup)
    const stats = lineup.stats || {}
    switch(column.normal) {
      case 'round1':
        return (stats.bouts && stats.bouts[0]) || 0
      case 'round2':
        return (stats.bouts && stats.bouts[1]) || 0
      case 'round3':
        return (stats.bouts && stats.bouts[2]) || 0
      case 'round4':
        return (stats.bouts && stats.bouts[3]) || 0
      case 'totalStroke':
        return stats.stroke || 0
      case 'rank1':
        return (stats.rank1 || 0) + (stats.rank1Major || 0)
      case 'rank2':
        return (stats.rank2 || 0) + (stats.rank2Major || 0)
      case 'rank3':
        return (stats.rank3 || 0) + (stats.rank3Major || 0)
      case 'finished':
        return (stats.finished || 0) + (stats.finishedMajor || 0)
      case 'shootingRelay':
      case 'shootingOther':
        return stats.allShootings
      case 'goal':
      case 'assist':
        var pp = stats[camelcase('power-play-' + column.normal)] || 0
        var sh = stats[camelcase('box-play-' + column.normal)] || 0
        var total = (stats[column.normal] || 0) + pp + sh
        return total
      case 'win': {
        const won =
          stats.winQ16 ||
          stats.winQ8 ||
          stats.winQ4 ||
          stats.winFinal ||
          stats.winBronzeFinal ||
          stats.winByDefault ||
          stats.win
        const lost =
          stats.startingTeamEvent ||
          stats.disqualified ||
          stats.loseByDefault ||
          stats.lose
        const draw =
          stats.draw
        return won ? '✓' : draw ? '=' : lost ?  '✗' : 0
      }
      case 'teamWin': {
        const won = stats.teamWinQ16 ||
          stats.teamWinQ8 ||
          stats.teamWinQ4 ||
          stats.teamWinFinal ||
          stats.teamWinBronzeFinal ||
          stats.teamWin
        return won ? '✓' : stats.disqualified ? '✗' : 0
      }
      case 'teamLoss': {
        if(stats.teamLoss) return '✗'
        return '-'
      }
      case 'teamVictory':
        if(stats.teamVictory) return '✓✓'
        if(stats.overtimeVictory) return '✓'
        if(stats.onWinningTeamTour) return '✓'
        if(stats.onWinningTeamRace) return '✓'
        return 0
      case 'attendance':
        if(stats.fullGame) return '✓✓'
        if(stats.started) return '✓'
        if(stats.substitution) return '+'
        return 0
      case 'dropGoal':
        return (stats.dropGoal || 0) + (stats.dropGoalPenalty || 0)
      case 'impact':
        var val = stats[column.normal]
        return val < 0 ? '-' : val > 0 ? '+' : 0
      case 'controlTime':
        return displayTime(stats[column.normal] || 0)
      case 'cleanSheet':
      case 'hillRecord':
      case 'longestRoundJump':
      case 'bestInTeam':
      case 'perfect':
      case 'firstManOfTheMatch':
      case 'secondManOfTheMatch':
      case 'thirdManOfTheMatch':
      case 'completeGame':
      case 'completeGameShutOut':
      case 'performanceOfTheNight':
      case 'fightOfTheNight':
      case 'tierWinnerPoints':
      case 'tierLoserPoints':
      case 'overrideTierWinner':
      case 'tripleTwelve':
      case 'juryWinnerPoints':
      case 'televiewerWinnerPoints':
      case 'auxiliary1':
      case 'auxiliary2':
      case 'auxiliary3':
      case 'aggressive':
        return stats[column.normal] ? '✓' : 0
      case 'forfeit':
      case 'disqualified':
      case 'gallop':
      case 'duckDismissal':
      case 'noFinishRegular':
      case 'noFinishCombined':
      case 'noFinishDownhill':
        return stats[column.normal] === 1 ? '✗' : (stats[column.normal] || '')
      case 'lane':
      case 'economyRate':
      case 'strikeRate':
        return lineup[column.normal]
      case 'over': {
        const value = +lineup.stats[column.normal] || 0
        return +`${Math.floor(value)}.${Math.round(value * 6) % 6}`
      }
      case 'qualified': {
        if(this.match.sport === 'eurovision') {
          return stats[column.normal] ? '✓' : 0
        }
        if(this.match.score) {
          const [first, second] = this.match.score
          if(first === second) return 0
          const team = first > second ? this.match.homeTeam : this.match.awayTeam
          return team._id === lineup.teamId ? '✓' : 0
        }
        return 0
      }
      case 'winRound':
        return stats[column.normal] === -1 ? '✓' : stats[column.normal] || 0
      case 'winBy1Games':
      case 'winBy2Games':
      case 'winBy3Games':
      case 'winBy4Games':
      case 'winBy5Games':
      case 'winBy6Games':
      case 'doubleFault':
      case 'breakWon':
        return (
          (stats[`${column.normal}`] || 0) ||
          ((stats[`${column.normal}3`] || 0) +
          (stats[`${column.normal}5`] || 0))
        )
      case 'ace':
        if(this.match.sport === 'csgo') {
          return stats[column.normal] || 0
        } else {
          return (
            (stats[`${column.normal}`] || 0) ||
            ((stats[`${column.normal}3`] || 0) +
            (stats[`${column.normal}5`] || 0))
          )
        }
      case 'won': {
        const value =
          (stats[column.normal] || 0) +
          (stats[`${column.normal}3`] || 0) +
          (stats[`${column.normal}5`] || 0)
        return value === 1 ? '✓' : value || 0
      }
      case 'loseByRetirement': {
        const value =
          (stats[column.normal] || 0) +
          (stats[`${column.normal}3`] || 0) +
          (stats[`${column.normal}5`] || 0)
        return value === 1 ? '✗' : value || 0
      }
      case 'straightSetsBonus': {
        const value = stats[column.normal] || 0
        return value ? `${value}x` : 0
      }
      case 'conversion':
        return (stats.converrsion || 0) +
          (stats.conversionReturn)
      case 'allFourSub70':
      case 'madeCut':
        return stats[column.normal] < 0 ? '✗' : stats[column.normal] === 1 ? '✓' : '-'
      default:
        return stats[column.normal] || 0
    }
  }
  calcPoints(lineup) {
    lineup.stats.point =
        (lineup.stats.threepointGoal * 3) +
        (lineup.stats.fieldGoal * 2) +
        lineup.stats.freethrowGoal
    this.render()
  }
  calcDoubles(lineup) {
    var count = 0
    doubleStats.forEach(d => { if(lineup.stats[d] > 9) count++ })
    lineup.stats.doubleDouble = count === 2
    lineup.stats.tripleDouble = count > 2
    this.render()
  }
  get columns() {
    /* eslint-disable object-curly-spacing */
    switch(this.match.sport) {
      case 'football':
        if(this.match.status === 'pending') return [{normal: 'projectedPoints', full: 'Projected Points', abbr: 'PP'}]
        return [
          {normal: 'goal', full: 'Goals', abbr: 'G'},
          {normal: 'assist', full: 'Assists', abbr: 'A'},
          {normal: 'cleanSheet', full: 'Clean Sheet', abbr: 'CS'},
          {normal: 'concededGoal', full: 'Conceded Goals', abbr: 'CG'},
          {normal: 'ownGoal', full: 'Own Gaol', abbr: 'OG'},
          {normal: 'keeperSave', full: 'Saves', abbr: 'S'},
          {normal: 'penaltySave', full: 'Penalties Saved', abbr: 'PS'},
          {normal: 'penaltyMiss', full: 'Penalties Missed', abbr: 'PM'},
          {normal: 'foulCommited', full: 'Foul Commited', abbr: 'FC'},
          {normal: 'foulsDrawn', full: 'Foul Drawn', abbr: 'FD'},
          {normal: 'yellowCard', full: 'Yellow Cards', abbr: 'YC'},
          {normal: 'redCard', full: 'Red Cards', abbr: 'RC'},
          {normal: 'impact', full: 'Impact', abbr: 'I'},
          {normal: 'bonus', full: 'Bonus', abbr: '🏅'},
          {normal: 'shotOnTarget', full: 'Shots on target', abbr: 'SoG'},
          {normal: 'allShotsOnTarget', full: 'All Shots on target (incl. goals)', abbr: 'FSoG'},
          {normal: 'shootoutCaused', full: 'Shootout caused', abbr: 'SC'},
          {normal: 'penaltyCaused', full: 'Penalty caused', abbr: 'PC'},
        ]
      case 'handball':
        return [
          {normal: 'goal', full: 'Goals', abbr: 'G'},
          {normal: 'miss', full: 'Misses', abbr: 'M'},
          {normal: 'penaltyMiss', full: 'Penalties Missed', abbr: 'PM'},
          {normal: 'penaltySave', full: 'Penalties Saved', abbr: 'PS'},
          {normal: 'steal', full: 'Steal', abbr: 'ST'},
          {normal: 'block', full: 'Blocks', abbr: 'BLK'},
          {normal: 'penaltyInduce', full: 'Penalties Induced', abbr: 'PI'},
          {normal: 'save', full: 'Saves', abbr: 'S'},
          {normal: 'concededGoal', full: 'Conceded Goals', abbr: 'CG'},
          {normal: 'yellowCard', full: 'Yellow Cards', abbr: 'YC'},
          {normal: 'redCard', full: 'Red Cards', abbr: 'RC'},
          {normal: 'twoMinute', full: 'Two Minute Penalty', abbr: 'TM'},
          {normal: 'teamLoss', full: 'Team Loss', abbr: 'TL'},
          {normal: 'teamWin', full: 'Team Win', abbr: 'TW'},
        ]
      case 'hockey':
        return this.position === 'goalkeeper' ? [
          {normal: 'goal', full: 'Goals', abbr: 'G'},
          {normal: 'assist', full: 'Assists', abbr: 'A'},
          {normal: 'penaltyMinutes', full: 'Penalty Minutes', abbr: 'PM'},
          {normal: 'shot', full: 'Shots', abbr: 'SH'},
          {normal: 'keeperSave', full: 'Saves', abbr: 'S'},
          {normal: 'concededGoal', full: 'Goals Conceded', abbr: 'GC'},
          {normal: 'teamVictory', full: 'Team Victory', abbr: 'TV'},
          {normal: 'cleanSheet', full: 'Shutout', abbr: 'CS'},
          {normal: 'blockedShot', full: 'Blocked Shot', abbr: 'BS'},
          {normal: 'shootoutGoal', full: 'Shootout Goal', abbr: 'SG'},
          {normal: 'gameWinningGoal', full: 'Game Winning Goal', abbr: 'GWG'},
        ] : [
          {normal: 'goal', full: 'Goals', abbr: 'G'},
          {normal: 'assist', full: 'Assists', abbr: 'A'},
          {normal: 'penaltyMinutes', full: 'Penalty Minutes', abbr: 'PM'},
          {normal: 'shootoutCaused', full: 'Shootout Caused', abbr: 'SC'},
          {normal: 'hit', full: 'Hits', abbr: 'Hit'},
          {normal: 'shot', full: 'Shots', abbr: 'SH'},
          {normal: 'plusMinus', full: '+/-', abbr: '+/-'},
          {normal: 'blockedShot', full: 'Blocked Shot', abbr: 'BS'},
          {normal: 'shootoutGoal', full: 'Shootout Goal', abbr: 'SG'},
          {normal: 'gameWinningGoal', full: 'Game Winning Goal', abbr: 'GWG'}
        ]
      case 'basket':
        return [
          {normal: 'threepointGoal', full: '3-point goals', abbr: '3P'},
          {normal: 'fieldGoal', full: '2-points goals', abbr: '2P'},
          {normal: 'freethrowGoal', full: 'Freethrow goals', abbr: '1P'},
          {normal: 'point', full: 'Points scored', abbr: 'Pt'},
          {normal: 'rebound', full: 'Rebounds', abbr: 'RB'},
          {normal: 'assist', full: 'Assists', abbr: 'As'},
          {normal: 'steal', full: 'Steals', abbr: 'St'},
          {normal: 'block', full: 'Blocks', abbr: 'Bl'},
          {normal: 'turnover', full: 'Turnovers', abbr: 'TO'},
        ]
      case 'golf':
        return this.match.matchType === 'one_round' ? [
          {normal: 'betterThanEagle', full: 'Eagle (2 below)', abbr: 'BTE'},
          {normal: 'eagle', full: 'Eagle (2 below)', abbr: 'E'},
          {normal: 'birdie', full: 'Birdie (1 below)', abbr: 'B'},
          {normal: 'par', full: 'Par', abbr: 'P'},
          {normal: 'bogey', full: 'Bogey (1 above)', abbr: 'Bo'},
          {normal: 'doubleBogey', full: 'Bogey (1 above)', abbr: 'DBo'},
          {
            normal: 'tripleBogeyOrWorse',
            full: 'Triple bogey or worse',
            abbr: 'TBo',
          },
          {normal: 'noBogeys', full: 'No bogeys', abbr: 'NB'},
          {normal: 'threeConsecutiveBirdies', full: 'Three consecutive birdies', abbr: '3CB'},
          {normal: 'ro64', full: '', abbr: 'R64'},
          {normal: 'bounceBack', full: 'Bounce back', abbr: 'BB'},
          {normal: 'remainingHoles', full: 'Remaining Holes', abbr: 'RH'},
        ] : [
          {normal: 'rank', full: 'Rank', abbr: 'R'},
          {normal: 'holeInOne', full: 'Hole In One', abbr: 'HIO'},
          {normal: 'betterThanEagle', full: 'Eagle (2 below)', abbr: 'BTE'},
          {normal: 'eagle', full: 'Eagle (2 below)', abbr: 'E'},
          {normal: 'birdie', full: 'Birdie (1 below)', abbr: 'B'},
          {normal: 'par', full: 'Par', abbr: 'P'},
          {normal: 'bogey', full: 'Bogey (1 above)', abbr: 'Bo'},
          {normal: 'doubleBogey', full: 'Bogey (1 above)', abbr: 'DBo'},
          {
            normal: 'tripleBogeyOrWorse',
            full: 'Triple bogey or worse',
            abbr: 'TBo',
          },
          {normal: 'noBogeys', full: 'No bogeys', abbr: 'NB'},
          {normal: 'threeConsecutiveBirdies', full: 'Three consecutive birdies', abbr: '3CB'},
          {normal: 'ro64', full: '', abbr: 'R64'},
          {normal: 'allFourSub70', full: 'All four sub-70s', abbr: '4S70'},
          {normal: 'bounceBack', full: 'Bounce back', abbr: 'BB'},
          {normal: 'remainingHoles', full: 'Remaining Holes', abbr: 'RH'},
          {normal: 'madeCut', full: 'Made the cut', abbr: 'C'},
        ]
      case 'rocket_runout':
        return this.match.matchType === 'one_round' ? [
          {normal: 'ballPocketed', full: 'Balls pocketed', abbr: 'BP'},
          {normal: 'breakAndRun', full: 'Break and run', abbr: 'BR'},
          {normal: 'dryBreakAndRun', full: 'Dry break and run', abbr: 'DBR'},
          {normal: 'ballInHandAndRun', full: 'Ball in hand and run', abbr: 'BIHR'},
          {normal: 'salvageRun', full: 'Salvage run', abbr: 'SR'},
          {normal: 'partialRun1', full: 'partial run 1', abbr: 'PR1'},
          {normal: 'partialRun2', full: 'partial run 2', abbr: 'PR2'},
          {normal: 'partialRun3', full: 'partial run 3', abbr: 'PR3'},
          {normal: 'partialRun4', full: 'partial run 4', abbr: 'PR4'},
          {normal: 'partialRun5', full: 'partial run 5', abbr: 'PR5'},
          {normal: 'combination', full: 'Combinations', abbr: 'C'},
          {normal: 'special', full: 'Specials', abbr: 'S'},
          {normal: 'perfectGame', full: 'Perfect games', abbr: 'PG'},
          {normal: 'scratch', full: 'Scratches', abbr: 'SCR'},
          {normal: 'foul', full: 'Fouls', abbr: 'F'},
          {normal: 'remainingFrames', full: 'Remaining frames', abbr: 'RF'},
          {normal: 'gameScore', full: 'Game Score', abbr: 'GS'},
        ] : [
          {normal: 'rank', full: 'Rank', abbr: 'R'},
          {normal: 'ballPocketed', full: 'Balls pocketed', abbr: 'BP'},
          {normal: 'breakAndRun', full: 'Break and run', abbr: 'BR'},
          {normal: 'dryBreakAndRun', full: 'Dry break and run', abbr: 'DBR'},
          {normal: 'ballInHandAndRun', full: 'Ball in hand and run', abbr: 'BIHR'},
          {normal: 'salvageRun', full: 'Salvage run', abbr: 'SR'},
          {normal: 'partialRun1', full: 'partial run 1', abbr: 'PR1'},
          {normal: 'partialRun2', full: 'partial run 2', abbr: 'PR2'},
          {normal: 'partialRun3', full: 'partial run 3', abbr: 'PR3'},
          {normal: 'partialRun4', full: 'partial run 4', abbr: 'PR4'},
          {normal: 'partialRun5', full: 'partial run 5', abbr: 'PR5'},
          {normal: 'combination', full: 'Combinations', abbr: 'C'},
          {normal: 'special', full: 'Specials', abbr: 'S'},
          {normal: 'perfectGameCount', full: 'Perfect games', abbr: 'PG'},
          {normal: 'scratch', full: 'Scratches', abbr: 'SCR'},
          {normal: 'foul', full: 'Fouls', abbr: 'F'},
          {normal: 'remainingFrames', full: 'Remaining frames', abbr: 'RF'},
          {normal: 'gameScore', full: 'Game Score', abbr: 'GS'},
        ]
      case 'cycling':
        return this.match.bout || this.position === 'manager' ? [
          {
            normal: 'rank',
            full: 'Rank',
            abbr: 'Rank',
          },
        ] : [
          {
            normal: 'rank',
            full: 'Rank',
            abbr: 'Rank',
          },
          {
            normal: 'aggressive',
            full: 'Most aggressive',
            abbr: 'aggr',
            type: 'bool',
          },
          {
            normal: 'shirtEarned',
            full: 'Shirt earned',
            abbr: 'shirt',
            enum: [
              'yellow',
              'green',
              'climber',
              'youth',
            ],
          },
        ]
      case 'cycling_vm':
        return [
          {normal: 'rank', full: 'Ranking', abbr: 'Rank'},
        ]
      case 'dota2':
        return [
          {normal: 'firstBlood', full: 'First blood kills', abbr: 'FB'},
          {normal: 'kill', full: 'Kills', abbr: 'K'},
          {normal: 'death', full: 'Deaths', abbr: 'D'},
          {normal: 'assist', full: 'Assists', abbr: 'A'},
          {normal: 'creepKill', full: 'Creep Kills', abbr: 'CK'},
          {normal: 'creepDeny', full: 'Creep Denies', abbr: 'CD'},
          {normal: 'towerDamage', full: 'Tower Damage', abbr: 'BD'},
          {normal: 'roshanKill', full: 'Roshan Kills', abbr: ' R'},
          {normal: 'roshanKillTeamBonus', full: 'Roshan Kills Team Bonus', abbr: 'RTB'},
          {normal: 'aegisSnatched', full: 'Aegis Snatched', abbr: 'AS'},
          {normal: 'wardsPlaced', full: 'Wards placed', abbr: 'WP'},
          {normal: 'wardsDestroyed', full: 'Wards destroyed', abbr: 'WD'},
          {normal: 'campStack', full: 'Camps stacked', abbr: 'CS'},
          {normal: 'coreCampStack', full: 'Camps stacked (Core)', abbr: 'CS'},
          {normal: 'runePickup', full: 'Rune pickups', abbr: 'RP'},
          {normal: 'courierKill', full: 'Couriers killed', abbr: 'CK'},
          {normal: 'stun', full: 'Stuns', abbr: 'S'},
          {normal: 'heroHealing', full: ' Hero healing', abbr: 'HH'},
          {normal: 'underTwentySeven', full: '<27', abbr: '<27'},
          {normal: 'underThirtyThree', full: '<33', abbr: '<33'},
          {normal: 'overFortyFive', full: '>45', abbr: '>45'},
          {normal: 'overFiftyFive', full: '>55', abbr: '>55'},
          {normal: 'overSeventy', full: '>70', abbr: '>70'},
        ]
      case 'horses':
        return [
          {normal: 'batch', full: 'Batch', abbr: 'Batch'},
          {normal: 'lane', full: 'Lane', abbr: 'Start'},
          {normal: 'rank', full: 'Ranking', abbr: 'Rank'},
          {normal: 'prizeMoney', full: 'Prize Money', abbr: 'Prize'},
          {normal: 'gallop', full: 'Gallop', abbr: 'G'},
          {normal: 'disqualified', full: 'Disqualified', abbr: 'DQ'},
          {normal: 'forfeit', full: 'Struck', abbr: '-'},
          {normal: 'kmTime', full: 'kmTime', abbr: 'kmt'},
        ]
      case 'alpine':
        return this.match.matchType === 'team' && this.position === 'manager' ? [
          {normal: 'pair', full: 'Pair', abbr: 'P'},
        ] : this.match.matchType === 'team' ? [
          {normal: 'pair', full: 'Pair', abbr: 'P'},
          {normal: 'win', full: 'Won', abbr: '🏅'},
          {normal: 'teamWin', full: 'Team Won', abbr: '🏆'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : this.position === 'manager' ? [
          {normal: 'rank', full: 'Ranking', abbr: '#'},
        ] : this.match.matchType === 'individual_duel' ? [
          {normal: 'pair', full: 'Pair', abbr: 'P'},
          {normal: 'win', full: 'Won', abbr: '🏅'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : this.match.matchType === 'combined' ? [
          {normal: 'combinedRank', full: 'Ranking', abbr: '#'},
          {normal: 'combinedDownhillRank', full: 'Downhill Ranking', abbr: 'Downhill'},
          {normal: 'combinedDownhillSpeedRank', full: 'Downhill Speed Ranking', abbr: 'DSpeed'},
          {normal: 'combinedSlalomRank', full: 'Combined Slalom Ranking', abbr: 'Slalom'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : this.match.matchType === 'downhill' ? [
          {normal: 'downhillRank', full: 'Ranking', abbr: '#'},
          {normal: 'downhillSplitRank', full: 'Downhill Split Ranking', abbr: '#SR'},
          {normal: 'downhillSpeedRank', full: 'Downhill Speed Ranking', abbr: '#DSR'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : [
          {normal: 'rank', full: 'Ranking', abbr: '#'},
          {normal: 'splitRank', full: 'Split Ranking', abbr: '#SR'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ]
      case 'cc_ski':
        return this.position === 'manager' ? [
          {normal: 'rank', full: 'Ranking', abbr: '#'},
        ] : this.match.matchType === 'tour' ? [
          {normal: 'tourRank', full: 'Ranking', abbr: '#'},
        ] : this.match.matchType === 'sprint' ? [
          {normal: 'sprintPrologRank', full: 'Prolog Rank', abbr: 'Prolog #'},
          {normal: 'sprintQuarterRank', full: 'Quarter Rank', abbr: 'Quarter #'},
          {normal: 'sprintSemiRank', full: 'Semifinal Rank', abbr: 'Semifinal #'},
          {normal: 'sprintFinalRank', full: 'Final Rank', abbr: 'Final #'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : this.match.matchType === 'qualification' ? [
          {normal: 'qualificationRank', full: 'Qualification Rank', abbr: 'Qualification #'},
          {normal: 'qualificationSplitRank', full: 'Qualification Split', abbr: 'Qualification #'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : this.match.matchType === 'team_sprint' ? [
          {normal: 'teamSprintSemifinalRank', full: 'Semifinal Rank', abbr: 'SF#'},
          {normal: 'teamSprintSemifinalTimeRank', full: 'Semifinal Time Rank', abbr: 'SFT#'},
          {normal: 'teamSprintFinalRank', full: 'Final Rank', abbr: 'F#'},
          {normal: 'teamSprintFinalTimeRank', full: 'Final Time Rank', abbr: 'FT#'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : this.match.matchType === 'relay' ? [
          {normal: 'relayTeamRank', full: 'Relay Team Rank', abbr: '#'},
          {normal: 'relayTimeRank', full: 'Relay Time Rank', abbr: 'Time#'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : this.match.matchType === 'mass_start_men' ? [
          {normal: 'massStartRank', full: 'Rank (Mass Start)', abbr: '#'},
          {normal: 'massStartMenSplit1Rank', full: 'First Split Rank (Mass Start Men)', abbr: 'Split1'},
          {normal: 'massStartMenSplit2Rank', full: 'Second Rank (Mass Start Men)', abbr: 'Split2'},
          {normal: 'massStartMenSplit3Rank', full: 'Third Rank (Mass Start Men)', abbr: 'Split3'},
          {normal: 'massStartMenSplit4Rank', full: 'Fourth Rank (Mass Start Men)', abbr: 'Split4'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : this.match.matchType === 'mass_start_women' ? [
          {normal: 'massStartRank', full: 'Rank (Mass Start)', abbr: '#'},
          {normal: 'massStartWomenSplit1Rank', full: 'First Split Rank (Mass Start Women)', abbr: 'Split1'},
          {normal: 'massStartWomenSplit2Rank', full: 'Second Split Rank (Mass Start Women)', abbr: 'Split2'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : [
          {normal: 'rank', full: 'Ranking', abbr: '#'},
          {normal: 'splitRank', full: 'Split Ranking', abbr: 'Time#'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ]
      case 'curling':
        return [
          {normal: 'hitPercentage', full: 'Hit Percentage', abbr: 'Hit%'},
        ]
      case 'ski_jump':
        return [
          {normal: 'rank', full: 'Total', abbr: 'Total'},
          {normal: 'round1Rank', full: 'Round 1', abbr: 'R1'},
          {normal: 'round2Rank', full: 'Round 2', abbr: 'R2'},
          {normal: 'longestRoundJump', full: 'Longest Jump in Round', abbr: 'Longest'},
          {normal: 'hillRecord', full: 'Hill Record', abbr: 'Record'},
          {normal: 'distance', full: 'Length', abbr: 'm'},
          {normal: 'bestInTeam', full: 'Best in Team', abbr: 'Leader'},
        ]
      case 'biathlon':
        return this.position === 'manager' ? [
          {normal: 'rank', full: 'Ranking', abbr: '#'},
        ] : (this.match.matchType === 'mixed_relay' && /\b(stage)\b/.test(this.match.tag)) ? [
          {normal: 'mixedRelayRank', full: 'Mixed Relay Stage Rank', abbr: '#'},
          {normal: 'shootingRelay', full: 'Misses from shooting', abbr: 'Misses'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : this.match.matchType === 'mixed_relay' ? [
          {normal: 'mixedRelayTeamRank', full: 'Mixed Relay Team Rank', abbr: 'Team#'},
          {normal: 'onWinningTeamRace', full: 'Team Victory', abbr: 'TV'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : this.match.matchType === 'sprint' ? [
          {normal: 'sprintSplit1Rank', full: 'First Split Rank', abbr: 'Split1'},
          {normal: 'sprintSplit2Rank', full: 'Second Split Rank', abbr: 'Split2'},
          {normal: 'shootingOther', full: 'Misses', abbr: 'Misses'},
          {normal: 'rank', full: 'Rank', abbr: '#'},
          {normal: 'onWinningTeamRace', full: 'Team Victory', abbr: 'TV'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : this.match.matchType === 'pursuit' ? [
          {normal: 'pursuitSplit1ARank', full: 'First Pursuit Split', abbr: 'Split1'},
          {normal: 'pursuitSplit2ARank', full: 'Second Pursuit Split', abbr: 'Split2'},
          {normal: 'pursuitSplit1BRank', full: 'Third Pursuit Split', abbr: 'Split3'},
          {normal: 'pursuitSplit2BRank', full: 'Fourth Pursuit Split', abbr: 'Split4'},
          {normal: 'shootingOther', full: 'Misses', abbr: 'Misses'},
          {normal: 'rank', full: 'Rank', abbr: '#'},
          {normal: 'onWinningTeamRace', full: 'Team Victory', abbr: 'TV'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : this.match.matchType === 'individual' ? [
          {normal: 'individualSplit1Rank', full: 'First Individual Split', abbr: 'Split1'},
          {normal: 'individualSplit2Rank', full: 'Second Individual Split', abbr: 'Split2'},
          {normal: 'individualSplit3Rank', full: 'Third Individual Split', abbr: 'Split3'},
          {normal: 'individualSplit4Rank', full: 'Fourth Individual Split', abbr: 'Split4'},
          {normal: 'shootingOther', full: 'Misses', abbr: 'Misses'},
          {normal: 'rank', full: 'Rank', abbr: '#'},
          {normal: 'onWinningTeamRace', full: 'Team Victory', abbr: 'TV'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : (this.match.matchType === 'single_mixed' && /\b(stage)\b/.test(this.match.tag)) ? [
          {normal: 'singleMixedRank', full: 'Single Mixed Rank', abbr: '#'},
          {normal: 'shootingOther', full: 'Misses', abbr: 'Misses'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : this.match.matchType === 'single_mixed' ? [
          {normal: 'singleMixedTeamRank', full: 'Single Mixed Team Rank', abbr: '#'},
          {normal: 'onWinningTeamRace', full: 'Team Victory', abbr: 'TV'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : (this.match.matchType === 'relay' && /\b(stage)\b/.test(this.match.tag)) ? [
          {normal: 'relayRank', full: 'Relay Rank', abbr: '#'},
          {normal: 'shootingRelay', full: 'Misses', abbr: 'Misses'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : this.match.matchType === 'relay' ? [
          {normal: 'relayTeamRank', full: 'Relay Team Rank', abbr: '#'},
          {normal: 'onWinningTeamRace', full: 'Team Victory', abbr: 'TV'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : this.match.matchType === 'mass_start' ? [
          {normal: 'rank', full: 'Mass Start Rank)', abbr: '#'},
          {normal: 'massStartSplit1Rank', full: 'First Split', abbr: 'Split1'},
          {normal: 'massStartSplit2Rank', full: 'Second Split', abbr: 'Split2'},
          {normal: 'massStartSplit3Rank', full: 'Third Split', abbr: 'Split3'},
          {normal: 'massStartSplit4Rank', full: 'Fourth Split', abbr: 'Split4'},
          {normal: 'shootingOther', full: 'Misses', abbr: 'Misses'},
          {normal: 'onWinningTeamRace', full: 'Team Victory', abbr: 'TV'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : this.match.matchType === 'tour' ? [
          {normal: 'tourRank', full: 'Ranking', abbr: '#'},
          {normal: 'onWinningTourRace', full: 'Team Victory', abbr: 'TV'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
        ] : [
          {normal: 'rank', full: 'Ranking', abbr: '#'},
          {normal: 'shootingOther', full: 'Misses', abbr: 'Misses'},
          {normal: 'forfeit', full: 'Disqualified', abbr: 'DQ'},
          {normal: 'onWinningTeamRace', full: 'Team Victory', abbr: 'TV'},
        ]
      case 'cricket':
        if(this.context === 'ball') {
          return [
            {normal: 'ball', full: 'Balls', abbr: 'Balls'},
            {normal: 'run', full: 'Run', abbr: 'Runs'},
            {normal: 'boundary', full: 'Boundary', abbr: '4s'},
            {normal: 'six', full: 'Six', abbr: '6s'},
            {normal: 'duckDismissal', full: 'Dismissal for a Duck', abbr: 'DD'},
            {normal: 'strikeRate', full: 'Strike Rate', abbr: 'SR'},
          ]
        } else if(this.context === 'over') {
          return [
            {normal: 'over', full: 'Overs', abbr: 'O'},
            {normal: 'concededRun', full: 'Run Conceded', abbr: 'RC'},
            {normal: 'wicket', full: 'Wicket', abbr: 'W'},
            {normal: 'maidenOver', full: 'Maiden Over', abbr: 'MO'},
            {normal: 'economyRate', full: 'Economy Rate', abbr: 'ER'},
          ]
        } else {
          return [
            {normal: 'caught', full: 'Catch', abbr: 'C'},
            {normal: 'stumping', full: 'Stumping', abbr: 'S'},
            {normal: 'runOut', full: 'Run-Out', abbr: 'RO'},
            {normal: 'runOutThrown', full: 'Run-Out Thrown', abbr: 'ROT'},
            {normal: 'runOutCaught', full: 'Run-Out Caught', abbr: 'ROC'},
          ]
        }
      case 'rugby_union':
        return [
          {normal: 'tryGoal', full: 'Try Goal', abbr: 'TG'},
          {normal: 'conversion', full: 'Conversion', abbr: 'Cv'},
          {normal: 'assist', full: 'Assist', abbr: 'A'},
          {normal: 'dropGoal', full: 'Drop Goal', abbr: 'DG'},
          {normal: 'penaltyMiss', full: 'Penalty Miss', abbr: 'PM'},
          {normal: 'yellowCard', full: 'Yellow Card', abbr: 'YC'},
          {normal: 'redCard', full: 'Red Card', abbr: 'RC'},
          {normal: 'turnover', full: 'Turnover', abbr: 'TO'},
          {normal: 'turnoverConceded', full: 'Turnover Conceded', abbr: 'TOC'},
          {normal: 'tackle', full: 'Succesful Tackle', abbr: 'T'},
          {normal: 'meterGained', full: 'Meters Gained', abbr: 'm'},
          {normal: 'impact', full: 'Impact', abbr: 'I'},
          {normal: 'attendance', full: 'Attendance', abbr: 'At'},
        ]
      case 'tennis':
        return [
          {normal: 'winBy1Games', full: 'Win By 1 Game', abbr: 'W1'},
          {normal: 'winBy2Games', full: 'Win By 2 Games', abbr: 'W2'},
          {normal: 'winBy3Games', full: 'Win By 3 Games', abbr: 'W3'},
          {normal: 'winBy4Games', full: 'Win By 4 Games', abbr: 'W4'},
          {normal: 'winBy5Games', full: 'Win By 5 Games', abbr: 'W5'},
          {normal: 'winBy6Games', full: 'Win By 6 Games', abbr: 'W6'},
          {normal: 'ace', full: 'Aces', abbr: 'Ace'},
          {normal: 'doubleFault', full: 'Double Fault', abbr: 'DF'},
          {normal: 'breakWon', full: 'Breaks Won', abbr: 'BW'},
          {normal: 'breakLost', full: 'Breaks Lost', abbr: 'BL'},
          {normal: 'loseByRetirement', full: 'Retired', abbr: 'R'},
          {normal: 'won', full: 'Won Match', abbr: '🏆'},
          {normal: 'straightSetsBonus', full: 'Straight Sets Bonus', abbr: 'Bns'},
        ]
      case 'kabaddi':
        return [
          {normal: 'started', full: 'Starting 7', abbr: '7'},
          {normal: 'substitution', full: 'Substituted', abbr: 'P'},
          {normal: 'touch', full: 'Raid Touch', abbr: 'RT'},
          {normal: 'raid', full: 'Succesful Raid', abbr: 'R+'},
          {normal: 'raidOut', full: 'Unsuccesful Raid', abbr: 'R-'},
          {normal: 'raidEmpty', full: 'Empty Raid', abbr: 'R0'},
          {normal: 'raidBonus', full: 'Raid Bonus', abbr: 'RB'},
          {normal: 'raidSuper', full: 'Super Raid', abbr: 'SR'},
          {normal: 'tackle', full: 'Succesful Tackle', abbr: 'T+'},
          {normal: 'tackleSuper', full: 'Super Tackle', abbr: 'ST'},
          {normal: 'greenCard', full: 'Green Card', abbr: 'GC'},
          {normal: 'yellowCard', full: 'Yellow Card', abbr: 'YC'},
          {normal: 'redCard', full: 'Red Card', abbr: 'RC'},
        ]
      case 'futsal':
        if(this.match.matchType === 'tournament') {
          return [
            {normal: 'goal', full: 'Goal', abbr: 'G'},
            {normal: 'yellowCard', full: 'Yellow Card', abbr: 'YC'},
            {normal: 'redCard', full: 'Red Card', abbr: 'RC'},
            {normal: 'firstManOfTheMatch', full: 'First Man of the Match', abbr: '1MM'},
            {normal: 'secondManOfTheMatch', full: 'Second Man of the Match', abbr: '2MM'},
            {normal: 'cleanSheet', full: 'Clean Sheet', abbr: 'CS'},
            {normal: 'qualified', full: 'Qualified', abbr: '🏆'},
          ]
        } else {
          return [
          {normal: 'goal', full: 'Goal', abbr: 'G'},
          {normal: 'assist', full: 'Assist', abbr: 'A'},
          {normal: 'yellowCard', full: 'Yellow Card', abbr: 'YC'},
          {normal: 'redCard', full: 'Red Card', abbr: 'RC'},
          {normal: 'cleanSheet', full: 'Clean Sheet', abbr: 'CS'},
          {normal: 'penaltyMiss', full: 'Penalty Miss', abbr: 'PM'},
          {normal: 'savePenalty', full: 'Save Penalty', abbr: 'SP'},
          {normal: 'ownGoal', full: 'Own Goal', abbr: 'OG'},
          {normal: 'causePenalty', full: 'Cause Penalty', abbr: 'CP' },
          {normal: 'causeFreekickGoal', full: 'Cause Freekick Goal', abbr: 'CFG'}
          ]
        }
      case 'baseball':
        if(this.position === 'pitcher') {
          return [
            {normal: 'inningPitched', full: 'Pitched an Inning', abbr: 'P'},
            {normal: 'hitAgainst', full: 'Hit Against', abbr: 'HA'},
            {normal: 'earnedRun', full: 'Earned Run', abbr: 'ER'},
            {normal: 'lostBase', full: 'Base on Balls Against', abbr: 'BoBA'},
            {normal: 'strikeout', full: 'Strikeout', abbr: 'SO'},
            {normal: 'batterHit', full: 'Hit Batter', abbr: 'HB'},
            {normal: 'noHitter', full: 'No Hitter', abbr: 'NH'},
            {normal: 'teamWin', full: 'Team Win', abbr: 'TW'},
            {normal: 'completeGame', full: 'Complete Game', abbr: 'CG'},
            {normal: 'completeGameShutOut', full: 'Complete Game Shut Out', abbr: 'CGSO'},
          ]
        } else {
          return [
            {normal: 'single', full: 'Single', abbr: 'Single'},
            {normal: 'double', full: 'Double', abbr: 'Double'},
            {normal: 'triple', full: 'Triple', abbr: 'Triple'},
            {normal: 'homeRun', full: 'Home Run', abbr: 'HR'},
            {normal: 'rbi', full: 'Run Batted In', abbr: 'RBI'},
            {normal: 'scoredRun', full: 'Run Scored', abbr: 'RS'},
            {normal: 'baseOnBall', full: 'Base on Balls', abbr: 'BoB'},
            {normal: 'stolenBase', full: 'Stolen Base', abbr: 'SB'},
            {normal: 'hitByPitch', full: 'Hit by Pitch', abbr: 'HbP'},
          ]
        }
      case 'american_football':
        if(this.position !== 'defense_special') {
          return [
            {normal: 'passingTd', full: 'Passing Touchdown', abbr: 'PaT'},
            {normal: 'passingYards', full: 'Passing Yards', abbr: 'PY'},
            {normal: 'intercepted', full: 'Interception', abbr: 'INT'},
            {normal: 'rushingTd', full: 'Rushing Touchdown', abbr: 'RuT'},
            {normal: 'rushingYards', full: 'Rushing Yards', abbr: 'RuY'},
            {normal: 'receivingTd', full: 'Receiving Touchdown', abbr: 'ReT'},
            {normal: 'receivingYards', full: 'Receiving Yards', abbr: 'RY'},
            {normal: 'reception', full: 'Reception', abbr: 'Rec'},
            {normal: 'punt', full: 'Punt', abbr: 'P'},
            {normal: 'fumbleLost', full: 'Fumble Lost', abbr: 'FL'},
            {normal: 'conversion', full: 'Conversion', abbr: 'Cnv'},
            {normal: 'returnTd', full: 'Return Touchdown', abbr: 'RT'},
            {normal: 'fumbleRecoveryTd', full: 'Fumble Recovery Touchdown', abbr: 'FRT'},
          ]
        } else {
          return [
            {normal: 'sack', full: 'Sack', abbr: 'Sck'},
            {normal: 'interception', full: 'Interception', abbr: 'INT'},
            {normal: 'fumbleRecovery', full: 'Fumble Recovery', abbr: 'FR'},
            {normal: 'fumbleRecoveryTd', full: 'Fumble Recovery Touchdown', abbr: 'FRT'},
            {normal: 'safety', full: 'Safety', abbr: 'Sft'},
            {normal: 'blockedKick', full: 'Blocked Kick', abbr: 'BK'},
            {normal: 'conversion', full: 'Conversion', abbr: 'Cnv'},
            {normal: 'returnTd', full: 'Return Touchdown', abbr: 'RT'},
            {normal: 'defensiveTd', full: 'Defensive Touchdown', abbr: 'DT'},
            {normal: 'pointsAllowed', full: 'Points Allowed', abbr: 'PA'},
            {normal: 'pointsAllowedOverride', full: 'Points Allowed Override', abbr: 'PAO'},
          ]
        }
      case 'mma':
        return [
          {normal: 'strike', full: 'Strike', abbr: 'Str'},
          {normal: 'significantStrike', full: 'Significant Strike', abbr: 'SStr'},
          {normal: 'controlTime', full: 'Control Time', abbr: 'CT'},
          {normal: 'takedown', full: 'Successful Takedown', abbr: 'TD'},
          {normal: 'reversal', full: 'Sweep/Reversal', abbr: 'S/Rv'},
          {normal: 'knockdown', full: 'Knockdown', abbr: 'KD'},
          {normal: 'winRound', full: 'Winning Round', abbr: 'WR'},
          {normal: 'winDecision', full: 'Win By Decision', abbr: 'WD'},
          {normal: 'quickWin', full: 'Quick Win', abbr: 'QW'},
        ]
      case 'csgo':
        return [
          {normal: 'kill', full: 'Enemy kill', abbr: 'K'},
          {normal: 'killHeadshot', full: 'Enemy Kill Headshot', abbr: 'HS'},
          {normal: 'killKnife', full: 'Enemy Kill Knife', abbr: 'KK'},
          {normal: 'killGrenade', full: 'Enemy Kill Grenade', abbr: 'GK'},
          {normal: 'teamkill', full: 'Teamkill', abbr: 'TK'},
          {normal: 'killAssist', full: 'Enemy Kill Assist', abbr: 'A'},
          {normal: 'flashAssist', full: 'Flash Kill Assist', abbr: 'FK'},
          {normal: 'death', full:'Death', abbr: 'D'},
          {normal: 'entryKill', full: 'Entry Kill', abbr: 'EK'},
          {normal: 'quad', full: 'Quad', abbr: 'Q'},
          {normal: 'ace', full: 'Ace', abbr: 'ACE'},
          {normal: 'bombPlanted', full: 'Bomb Planted', abbr: 'BP'},
          {normal: 'bombDefused', full: 'Bomb Defused', abbr: 'BD'},
          {normal: 'clutch1v1', full: 'Clutch 1v1', abbr: 'C1'},
          {normal: 'clutch1v2', full: 'Clutch 1v2', abbr: 'C2'},
          {normal: 'clutch1v3', full: 'Clutch 1v3', abbr: 'C3'},
          {normal: 'clutch1v4', full: 'Clutch 1v4', abbr: 'C4'},
          {normal: 'clutch1v5', full: 'Clutch 1v5', abbr: 'C5'},
          {normal: 'compensationPoints', full: 'Compensation Points', abbr: 'CP' },
          {normal: 'compensationPointsV3', full: 'Compensation Points V3', abbr: 'CPV3' },
          {normal: 'roundDifference', full: 'Round Difference', abbr: 'RD'},
          {normal: 'roundWin', full: 'Round Won', abbr: 'RW'},
          {normal: 'mapWin', full: 'Map Won', abbr: 'MW'},
        ]
      case 'e_football':
        return [
          {normal: 'win', full: 'Wins', abbr: 'W'},
          {normal: 'goal', full: 'Goals', abbr: 'G'},
          {normal: 'concededGoal', full: 'Conceded Goals', abbr: 'CG'},
          {normal: 'cleanSheet', full: 'Clean Sheet', abbr: 'CS'},
          {normal: 'comeback', full: 'Comeback', abbr: 'CB'},
          {normal: 'lastMinuteGoal', full: 'Last Minute Goal', abbr: 'LG'},
          {normal: 'freekickGoal', full: 'Freekick Goal', abbr: 'FG'},
          {normal: 'yellowCard', full: 'Yellow Cards', abbr: 'YC'},
          {normal: 'redCard', full: 'Red Cards', abbr: 'RC'},
          {normal: 'penaltySave', full: 'Penalties Saved', abbr: 'PS'},
          {normal: 'shotOnGoal', full: 'Shot on Goal', abbr: 'SG'}
        ]
      case 'lol':
        return [
          {normal: 'kill', full: 'Kills', abbr: 'K'},
          {normal: 'death', full: 'Deaths', abbr: 'D'},
          {normal: 'assist', full: 'Assists', abbr: 'A'},
          {normal: 'creepKill', full: 'Creep Kill', abbr: ' CK'},
          {normal: 'firstBlood', full: 'First Blood', abbr: 'FB'},
          {normal: 'turretTakedownTeam', full: 'Turret Takedown (Team)', abbr: 'TT'},
          {normal: 'baronTeamKill', full: 'Baron Team Kill', abbr: 'B'},
          {normal: 'dragonTeamKill', full: 'Dragon Team Kill', abbr: 'DK'},
          {normal: 'riftHeraldTeamKill', full: 'Rift Herald Team Kill', abbr: 'RHK'},
          {normal: 'win', full: 'Map Won', abbr: 'MW'},
          {normal: 'winInUnderThirtyMinutes', full: 'Win In Under Thirty Minutes', abbr: 'WU30'},
          {normal: 'winInUnderThirtyfiveMinutes', full: 'Win In Under Thirtyfive Minutes', abbr: 'WU35'},
          {normal: 'dragonSteal', full: 'Dragon Steal', abbr: 'DS'},
          {normal: 'baronSteal', full: 'Baron Steal', abbr: 'BS'},
        ]
      case 'formula_one':
        if(this.position === 'ctor') {
          if(this.match.matchType === 'qualifier') {
            return [
              {normal: 'qualifierTeamRank', full: 'Rank', abbr: '#'},
              {normal: 'qualifierCarsFinished', full: 'Cars Finished', abbr: '#F'},
            ]
          } else if(this.match.matchType === 'sprint') {
            return [
              {normal: 'sprintTeamRank', full: 'Rank', abbr: '#'},
              {normal: 'sprintCarsFinished', full: 'Cars Finished', abbr: '#F'},
            ]
          }
          return [
            {normal: 'teamRank', full: 'Rank', abbr: '#'},
            {normal: 'fastestPitStop', full: 'Fastest Pit Stop', abbr: 'FsP'},
            {normal: 'carsFinished', full: 'Cars Finished', abbr: '#F'},
          ]
        }
        if(this.match.matchType === 'qualifier') {
          return [
            {normal: 'rank', full: 'Rank', abbr: '#'},
          ]
        } else if(this.match.matchType === 'sprint') {
          return [
            {normal: 'rank', full: 'Rank', abbr: '#'},
            {normal: 'startingRank', full: 'Starting Rank', abbr: 'Start #'},
            {normal: 'sprintFastestLap', full: 'Fastest Lap', abbr: 'Fst'},
            {normal: 'sprintDriverOfTheDay', full: 'Driver of the Day', abbr: 'DotD'},
            {normal: 'sprintNoFinish', full: 'Disqualified', abbr: 'DQ'},
          ]
         }
        return [
          {normal: 'rank', full: 'Rank', abbr: '#'},
          {normal: 'startingRank', full: 'Starting Rank', abbr: 'Start #'},
          {normal: 'fastestLap', full: 'Fastest Lap', abbr: 'Fst'},
          {normal: 'driverOfTheDay', full: 'Driver of the Day', abbr: 'DotD'},
          {normal: 'disqualified', full: 'Disqualified', abbr: 'DQ'},
        ]
      case 'eurovision':
        if(this.match.matchType === 'qualifier') {
          return [
            {normal: 'qualified', full: 'Qualified', abbr: 'Q'},
          ]
        } else {
          return [
            {normal: 'broadcastPoints', full: 'Broadcast Points', abbr: 'BP'},
            {normal: 'broadcastRank', full: 'Broadcast Rank', abbr: 'BR'},
            {normal: 'juryPoints', full: 'Jury Points', abbr: 'P'},
            {normal: 'televiewerPoints', full: 'Televiewer Points', abbr: 'TV-P'},
            {normal: 'televiewerWinnerPoints', full: 'Televiewer Winner Points', abbr: 'TV-W'},
            {normal: 'tripleTwelve', full: 'tripleTwelve', abbr: 'TTT'},
            {normal: 'juryWinnerPoints', full: 'Jury Winner Points', abbr: 'JW-P'},
            {normal: 'tierWinnerPoints', full: 'Tier Winner Points', abbr: 'TierW'},
            {normal: 'tierLoserPoints', full: 'Tier Loser Points', abbr: 'TierL'},
            {normal: 'overrideTierWinner', full: 'Override Tier Check', abbr:'O' }
          ]
        }
      default:
        return []
    }
  }

  get state() {
    return getState(this.match)
  }

  get oldState() {
    return oldState(this.match)
  }

  get showPosition() {
    const match = this.match
    const positions = match.rulePositions
    return positions && positions.length > 1 &&
      match.sport !== 'ski_jump' &&
      (match.isDuel || match.isRacing) &&
      !this.position
  }

  // Debug function
  nullify() {
    this.lineups.forEach(l => {
      l.stats.rank = -1
    })
    this.render()
  }
}
